/**
 * Ukrainian phone mask and completeness validation for CF7 forms.
 *
 * Vanilla replacement for jquery.mask.js. Formats every `input[type="tel"]`
 * as `+380 (XX) XXX-XX-XX` and blocks the CF7 submit while the number is
 * shorter than the full 9 national digits.
 */
(function () {
    'use strict';

    var PREFIX = '+380';
    var NATIONAL_LENGTH = 9;

    var i18n = window.soloveyPhoneMask || {};
    var MSG_INCOMPLETE = i18n.incomplete || 'Введите номер телефона полностью.';
    var MSG_REQUIRED = i18n.required || 'Укажите номер телефона.';

    /**
     * Reduce free-form text to the national part of a Ukrainian number.
     *
     * Used for text that carries its own country code (a paste, a prefilled
     * value). For the field itself use nationalFromField(), which knows that
     * the mask template already prints one.
     *
     * @param {string} value Raw text.
     * @return {string} Up to 9 digits without the country code and leading zero.
     */
    function nationalDigits(value) {
        var digits = String(value).replace(/\D/g, '');

        if (digits.length > NATIONAL_LENGTH) {
            if (digits.indexOf('380') === 0) {
                digits = digits.slice(3);
            } else if (digits.indexOf('80') === 0) {
                digits = digits.slice(2);
            }
        }

        if (digits.indexOf('0') === 0) {
            digits = digits.slice(1);
        }

        return digits.slice(0, NATIONAL_LENGTH);
    }

    /**
     * Read the national part out of the field itself.
     *
     * Once the mask has printed its template the value already starts with the
     * country code, and those three digits must not be mistaken for typed ones
     * — otherwise the first keystroke lands behind "380" and the number is
     * rendered as +380 (38) 0…
     *
     * @param {string} value Current field value.
     * @return {string} Up to 9 national digits.
     */
    function nationalFromField(value) {
        var digits = String(value).replace(/\D/g, '');

        if (String(value).indexOf(PREFIX) === 0) {
            digits = digits.slice(3);
        }

        return nationalDigits(digits);
    }

    /**
     * Render the national digits as a masked phone number.
     *
     * @param {string} digits National digits (0-9 characters).
     * @return {string} Masked value, always starting with the country code.
     */
    function format(digits) {
        var out = PREFIX + ' (';

        if (!digits.length) {
            return out;
        }

        out += digits.slice(0, 2);

        if (digits.length >= 2) {
            out += ')';
        }
        if (digits.length > 2) {
            out += ' ' + digits.slice(2, 5);
        }
        if (digits.length > 5) {
            out += '-' + digits.slice(5, 7);
        }
        if (digits.length > 7) {
            out += '-' + digits.slice(7, 9);
        }

        return out;
    }

    /**
     * Count how many digits precede a caret position.
     *
     * @param {string} value Field value.
     * @param {number} caret Caret offset inside the value.
     * @return {number} Number of digits before the caret.
     */
    function digitsBefore(value, caret) {
        return value.slice(0, caret).replace(/\D/g, '').length;
    }

    /**
     * Find the caret offset that follows a given amount of digits.
     *
     * @param {string} value Masked value.
     * @param {number} count Digits that must stay before the caret.
     * @return {number} Caret offset inside the masked value.
     */
    function caretAfterDigits(value, count) {
        var seen = 0;
        var i;

        for (i = 0; i < value.length; i++) {
            if (/\d/.test(value[i])) {
                seen++;
                if (seen > count) {
                    return i;
                }
            }
        }

        return value.length;
    }

    /**
     * Whether the field holds a complete Ukrainian number.
     *
     * @param {HTMLInputElement} input Phone field.
     * @return {boolean} True when all 9 national digits are entered.
     */
    function isComplete(input) {
        return nationalFromField(input.value).length === NATIONAL_LENGTH;
    }

    /**
     * Whether the field is effectively empty (mask prefix only).
     *
     * @param {HTMLInputElement} input Phone field.
     * @return {boolean} True when no national digit is entered.
     */
    function isEmpty(input) {
        return nationalFromField(input.value).length === 0;
    }

    /**
     * Reformat the field and keep the caret next to the same digit.
     *
     * @param {HTMLInputElement} input Phone field.
     * @return {void}
     */
    function reformat(input) {
        var caret = input.selectionStart;
        var before = digitsBefore(input.value, caret === null ? input.value.length : caret);
        // The country code is part of the template, not of the typed digits.
        var offset = input.value.indexOf(PREFIX) === 0 ? 3 : 0;
        var digits = nationalFromField(input.value);

        input.value = format(digits);

        if (input === document.activeElement) {
            var position = caretAfterDigits(input.value, Math.max(before - offset, 0) + 3);
            try {
                input.setSelectionRange(position, position);
            } catch (e) {
                // Some browsers reject setSelectionRange on hidden fields.
            }
        }
    }

    /**
     * Remove the validation tip previously rendered for a field.
     *
     * @param {HTMLInputElement} input Phone field.
     * @return {void}
     */
    function clearError(input) {
        input.classList.remove('wpcf7-not-valid');
        input.removeAttribute('aria-invalid');

        var wrap = input.closest('.wpcf7-form-control-wrap') || input.parentNode;
        var tip = wrap ? wrap.querySelector('.wpcf7-not-valid-tip') : null;

        if (tip && tip.dataset.soloveyTip === '1') {
            tip.parentNode.removeChild(tip);
        }
    }

    /**
     * Render a CF7-styled validation tip under the field.
     *
     * @param {HTMLInputElement} input   Phone field.
     * @param {string}           message Message to show.
     * @return {void}
     */
    function showError(input, message) {
        clearError(input);

        input.classList.add('wpcf7-not-valid');
        input.setAttribute('aria-invalid', 'true');

        var wrap = input.closest('.wpcf7-form-control-wrap') || input.parentNode;
        if (!wrap) {
            return;
        }

        var tip = document.createElement('span');
        tip.className = 'wpcf7-not-valid-tip';
        tip.setAttribute('aria-hidden', 'true');
        tip.dataset.soloveyTip = '1';
        tip.textContent = message;
        wrap.appendChild(tip);
    }

    /**
     * Attach the mask to a single field.
     *
     * @param {HTMLInputElement} input Phone field.
     * @return {void}
     */
    function bind(input) {
        if (input.dataset.soloveyMask === '1') {
            return;
        }
        input.dataset.soloveyMask = '1';

        input.setAttribute('inputmode', 'tel');
        input.setAttribute('autocomplete', 'tel');
        input.setAttribute('maxlength', String(format('123456789').length));

        if (!isEmpty(input)) {
            reformat(input);
        }

        input.addEventListener('focus', function () {
            if (!input.value) {
                input.value = format('');
                var end = input.value.length;
                try {
                    input.setSelectionRange(end, end);
                } catch (e) {
                    // Ignore selection errors on unsupported inputs.
                }
            }
        });

        // A naive mask would "eat" a Backspace pressed on a separator, so the
        // deletion is applied to the digits themselves.
        input.addEventListener('keydown', function (event) {
            if (event.key !== 'Backspace' || input.selectionStart !== input.selectionEnd) {
                return;
            }

            var caret = input.selectionStart;
            var before = digitsBefore(input.value, caret);
            var prefixDigits = input.value.indexOf(PREFIX) === 0 ? Math.min(3, before) : 0;
            var nationalBefore = before - prefixDigits;

            // The country code is part of the template and cannot be deleted.
            event.preventDefault();

            if (nationalBefore === 0) {
                return;
            }

            var digits = nationalFromField(input.value);
            digits = digits.slice(0, nationalBefore - 1) + digits.slice(nationalBefore);

            input.value = format(digits);

            var position = caretAfterDigits(input.value, nationalBefore + 2);
            try {
                input.setSelectionRange(position, position);
            } catch (e) {
                // Ignore selection errors on unsupported inputs.
            }

            clearError(input);
        });

        input.addEventListener('input', function () {
            reformat(input);
            clearError(input);
        });

        input.addEventListener('blur', function () {
            // An untouched field must stay empty so the placeholder and the
            // CF7 "required" check keep working as usual.
            input.value = isEmpty(input) ? '' : format(nationalFromField(input.value));
        });
    }

    /**
     * Validate every phone field of a form.
     *
     * @param {HTMLFormElement} form CF7 form.
     * @return {boolean} True when all phone fields are complete.
     */
    function validateForm(form) {
        var fields = form.querySelectorAll('input[type="tel"]');
        var firstInvalid = null;

        Array.prototype.forEach.call(fields, function (input) {
            clearError(input);

            var required = input.classList.contains('wpcf7-validates-as-required') || input.required;

            if (isEmpty(input)) {
                if (required) {
                    showError(input, MSG_REQUIRED);
                    firstInvalid = firstInvalid || input;
                }
                return;
            }

            if (!isComplete(input)) {
                showError(input, MSG_INCOMPLETE);
                firstInvalid = firstInvalid || input;
            }
        });

        if (firstInvalid) {
            form.classList.add('invalid');
            firstInvalid.focus();
            return false;
        }

        return true;
    }

    /**
     * Initialise the mask and the submit guard.
     *
     * @return {void}
     */
    function init() {
        Array.prototype.forEach.call(document.querySelectorAll('input[type="tel"]'), bind);

        // Capture phase: CF7 binds its own submit handler on the form, so the
        // guard has to run before it to be able to cancel the request.
        document.addEventListener('submit', function (event) {
            var form = event.target;

            if (!form || !form.classList || !form.classList.contains('wpcf7-form')) {
                return;
            }

            if (!validateForm(form)) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
        }, true);

        // Forms injected later (CF7 replaces its markup after a submit).
        document.addEventListener('wpcf7submit', function () {
            Array.prototype.forEach.call(document.querySelectorAll('input[type="tel"]'), bind);
        }, false);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
